import { useState, useCallback } from 'react';
import { useLang } from '../../../hooks/useLang';

// ── 进度追踪 Hook ──
function useStepProgress() {
  const STORAGE_KEY = 'landing_step_progress';
  const [completed, setCompleted] = useState<Record<string, boolean>>(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); } catch { return {}; }
  });

  const toggle = useCallback((stepKey: string) => {
    setCompleted((prev) => {
      const next = { ...prev, [stepKey]: !prev[stepKey] };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setCompleted({});
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const totalItems = landingSteps.reduce((acc, s) => acc + s.items.length, 0);
  const completedCount = Object.values(completed).filter(Boolean).length;
  const percent = Math.round((completedCount / totalItems) * 100);

  return { completed, toggle, reset, completedCount, totalItems, percent };
}

const selfCheckQuestions = [
  { q: '资产法律权属是否已完成确权？', hint: '须提供土地证/产权证/债权合同' },
  { q: '是否为中国内地企业或机构？', hint: '须通过香港SPV架构合规参与' },
  { q: '资产是否存在抵押、查封或权属争议？', hint: '须在律师尽调时完全披露' },
  { q: '预计发行规模是否≥5000万港元？', hint: '低于此规模通常不具备经济可行性' },
  { q: '是否已初步接触SFC持牌机构？', hint: 'HashKey、OSL、HSBC Orion等' },
  { q: '是否已了解开曼SPV设立成本？', hint: '含律师费约15-30万港元' },
  { q: '是否有合格投资者认购意向？', hint: '仅限香港PI：净资产≥800万港元' },
  { q: '是否有能力承担合规持续成本？', hint: '年度审计+信息披露约30-50万港元/年' },
  { q: '是否了解资金回流的唯一合规通道？', hint: 'CIPS跨境支付或QFLP外商股权' },
  { q: '是否已准备好接受2年以上的周期规划？', hint: 'RWA项目从立项到落地通常≥12个月' },
];

const landingSteps = [
  {
    step: '01', duration: '4-8周', color: '#7C3AED',
    title: '资产尽调与法律架构设计',
    icon: 'ri-search-2-line',
    items: ['资产法律权属核查（土地证、产权证、债权合同）', '选择离岸架构（开曼SPV / BVI / 香港有限合伙）', '律师出具法律意见书（Legal Opinion）', '确认目标投资者群体（香港专业投资者/机构）'],
  },
  {
    step: '02', duration: '2-4周', color: '#A78BFA',
    title: '选择香港持牌通道与发行平台',
    icon: 'ri-building-2-line',
    items: ['对接SFC持牌法团（1类经纪/4类顾问/9类资管）', '选择代币化平台（Securitize/HashKey/OSL等）', '签署服务协议，确认承销安排', '选定托管机构（持牌托管人或信托公司）'],
  },
  {
    step: '03', duration: '4-6周', color: '#60A5FA',
    title: '智能合约开发与审计',
    icon: 'ri-code-s-slash-line',
    items: ['代币标准选择（ERC-1400安全代币标准）', 'KYC/AML白名单合约开发', '转让限制合约（仅限合格投资者）', '第三方智能合约安全审计（CertiK / Hacken）'],
  },
  {
    step: '04', duration: '2-3周', color: '#34D399',
    title: 'KYC/AML合规认证',
    icon: 'ri-shield-check-line',
    items: ['投资者KYC文件收集（护照、地址证明）', '专业投资者资格认证（资产证明≥800万港元）', 'AML/CFT筛查（PEP、制裁名单）', '投资者协议签署与链上白名单写入'],
  },
  {
    step: '05', duration: '2-4周', color: '#F59E0B',
    title: '代币发行与上市',
    icon: 'ri-rocket-line',
    items: ['招股说明书/发行通函准备与SFC备案', '代币铸造（Minting）与智能合约部署', '投资者认购与资金托管', '代币交付与二级市场上市（如适用）'],
  },
  {
    step: '06', duration: '持续', color: '#F472B6',
    title: '持续合规运营与报告',
    icon: 'ri-file-chart-line',
    items: ['季度/年度财务审计与披露', '代币持有人报告定期发送', '链上事件监控与异常预警', '监管动态跟踪，及时更新合规安排'],
  },
];

const industries = [
  { icon: 'ri-building-line', name: '房地产', sub: '住宅 / 商业 / 工业', steps: '产权公证 → 开曼SPV → ERC-1400代币 → SFC备案', priority: '优先赛道', color: '#34D399' },
  { icon: 'ri-plant-line', name: '农业与粮食', sub: '粮食仓单 / 农地流转', steps: '仓单存证 → 联盟链溯源 → 供应链融资代币化', priority: '鼓励类', color: '#60A5FA' },
  { icon: 'ri-car-line', name: '汽车与交通', sub: '汽车资产包 / ABS', steps: '汽车贷款ABS → SPV隔离 → 证券代币发行', priority: '鼓励类', color: '#A78BFA' },
  { icon: 'ri-hospital-line', name: '医疗健康', sub: '应收账款 / 医疗设备', steps: '医院应收款 → 数字化债权 → 机构LP认购', priority: '探索类', color: '#FB923C' },
  { icon: 'ri-sun-line', name: '清洁能源', sub: '光伏电站 / 风电收益', steps: '电站资产包 → 绿色债券代币 → 碳信用绑定', priority: '优先赛道', color: '#F59E0B' },
  { icon: 'ri-ship-line', name: '航运物流', sub: '提单 / 仓单 / 船舶', steps: '纸质单据数字化 → 区块链提单 → 贸易融资', priority: '鼓励类', color: '#38BDF8' },
  { icon: 'ri-artboard-line', name: '文化艺术', sub: '名画 / 版权 / 影视', steps: '独立估值 → NFT/STO发行 → 版权分成代币', priority: '探索类', color: '#C084FC' },
  { icon: 'ri-leaf-line', name: '碳资产与ESG', sub: '碳信用 / 绿色证书', steps: 'VCS/GS核证 → 链上铸造BCT → DeFi流动性', priority: '优先赛道', color: '#4ADE80' },
];

const priorityColor: Record<string, string> = {
  优先赛道: 'bg-emerald-50 text-emerald-700 border border-emerald-100',
  鼓励类: 'bg-[#7C3AED]/8 text-[#7C3AED] border border-[#7C3AED]/15',
  探索类: 'bg-amber-50 text-amber-700 border border-amber-100',
};

export default function LandingProcess() {
  const { t } = useLang();
  const { completed, toggle, reset, completedCount, totalItems, percent } = useStepProgress();
  const [showProgress, setShowProgress] = useState(false);

  return (
    <>
      {/* ======= 07 企业落地SOP ======= */}
      <section id="landing" className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-full mb-4">
              <i className="ri-route-line text-[#7C3AED] text-sm"></i>
              <span className="text-[#7C3AED] text-sm font-semibold">{t('07 · 企业落地全流程SOP', '07 · Enterprise RWA Full SOP')}</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">{t('企业 RWA 代币化落地 SOP', 'Enterprise RWA Tokenization SOP')}</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">
              {t(
                '从资产尽调到持续合规运营，完整六步指引，助企业在香港合规框架内完成RWA发行。',
                'From asset due diligence to ongoing compliance operations — a complete six-step guide for enterprises to complete RWA issuance within the Hong Kong regulatory framework.'
              )}
            </p>
          </div>

          {/* Self-check */}
          <div className="mb-12 rounded-2xl overflow-hidden border border-[#7C3AED]/15">
            <div className="px-6 py-4 flex items-center gap-3" style={{ background: 'linear-gradient(90deg, #2E1065, #4C1D95)' }}>
              <div className="w-9 h-9 flex items-center justify-center bg-white/15 rounded-xl">
                <i className="ri-question-answer-line text-white text-lg"></i>
              </div>
              <div>
                <div className="text-white font-black">{t('资产准入十问自测表', 'Asset Eligibility Self-Assessment (10 Questions)')}</div>
                <div className="text-white/55 text-xs">{t('在启动RWA项目前，逐条自测。全部"是"才建议推进。', 'Before launching an RWA project, check each item. Only proceed if all answers are "Yes".')}</div>
              </div>
            </div>
            <div className="bg-[#F5F3FF] p-5 grid md:grid-cols-2 gap-2">
              {selfCheckQuestions.map((q, i) => (
                <div key={i} className="flex items-start gap-3 bg-white border border-[#7C3AED]/8 rounded-lg px-4 py-3 hover:border-[#7C3AED]/25 transition-all">
                  <div className="w-6 h-6 flex items-center justify-center bg-[#7C3AED]/10 rounded-md shrink-0 mt-0.5">
                    <span className="text-[#7C3AED] font-black text-xs">{i + 1}</span>
                  </div>
                  <div className="flex-1">
                    <div className="text-gray-800 text-sm font-medium leading-snug">{q.q}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{q.hint}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Steps Header + Progress Bar */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h3 className="text-xl font-black text-gray-800 flex items-center gap-2">
              <div className="w-1 h-5 bg-[#7C3AED] rounded-full" />
              {t('六步完整流程（预计总周期 4-6 个月）', 'Six-Step Full Process (Estimated 4–6 Months Total)')}
            </h3>
            <div className="flex items-center gap-3">
              {completedCount > 0 && (
                <button
                  onClick={reset}
                  className="text-xs text-gray-400 hover:text-red-500 cursor-pointer transition-colors whitespace-nowrap"
                >
                  重置进度
                </button>
              )}
              <button
                onClick={() => setShowProgress(!showProgress)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  showProgress ? 'bg-[#7C3AED] text-white' : 'bg-white border border-[#7C3AED]/30 text-[#7C3AED] hover:bg-[#7C3AED]/5'
                }`}
              >
                <i className="ri-checkbox-multiple-line"></i>
                进度追踪模式
                {completedCount > 0 && (
                  <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${showProgress ? 'bg-white/20 text-white' : 'bg-[#7C3AED]/10 text-[#7C3AED]'}`}>
                    {percent}%
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Progress Overview */}
          {showProgress && (
            <div className="mb-6 bg-white border border-[#7C3AED]/15 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <i className="ri-route-line text-[#7C3AED]"></i>
                  <span className="font-bold text-gray-800 text-sm">我的落地进度</span>
                </div>
                <span className="text-sm font-black text-[#7C3AED]">{completedCount} / {totalItems} 项已完成</span>
              </div>
              {/* Progress Bar */}
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${percent}%`,
                    background: 'linear-gradient(90deg, #7C3AED, #F59E0B)',
                  }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  {percent === 0 && '点击步骤中的任务项开始追踪进度'}
                  {percent > 0 && percent < 50 && '继续加油，已完成不少了！'}
                  {percent >= 50 && percent < 100 && '过半了！坚持完成剩余步骤'}
                  {percent === 100 && '🎉 恭喜！所有步骤已完成，可以启动发行了！'}
                </span>
                <span className="font-bold text-[#7C3AED]">{percent}%</span>
              </div>
              {/* Step Progress Pills */}
              <div className="flex flex-wrap gap-2 mt-3">
                {landingSteps.map((step) => {
                  const stepCompleted = step.items.filter((_, j) => completed[`${step.step}-${j}`]).length;
                  const stepTotal = step.items.length;
                  const isDone = stepCompleted === stepTotal;
                  return (
                    <div
                      key={step.step}
                      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${isDone ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
                      style={isDone ? { backgroundColor: step.color } : {}}
                    >
                      {isDone ? <i className="ri-check-line text-xs"></i> : <span className="text-gray-400">Step {step.step}</span>}
                      <span>{isDone ? step.title.slice(0, 8) + '...' : `${stepCompleted}/${stepTotal}`}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {landingSteps.map((step) => {
              const stepCompletedCount = step.items.filter((_, j) => completed[`${step.step}-${j}`]).length;
              const isStepDone = stepCompletedCount === step.items.length;
              return (
              <div
                key={step.step}
                className={`border rounded-2xl p-6 hover:-translate-y-0.5 transition-all ${
                  isStepDone
                    ? 'bg-white border-emerald-200'
                    : 'bg-[#F5F3FF] border-[#7C3AED]/10 hover:border-[#7C3AED]/30'
                }`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl shrink-0 relative" style={{ background: `${step.color}20`, border: `1px solid ${step.color}30` }}>
                    <i className={`${step.icon} text-xl`} style={{ color: step.color }}></i>
                    {isStepDone && (
                      <div className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-emerald-500 rounded-full">
                        <i className="ri-check-line text-white text-xs" style={{ fontSize: '9px' }}></i>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-gray-400 font-medium">Step {step.step}</div>
                      <div className="flex items-center gap-1.5">
                        {showProgress && (
                          <span className="text-xs font-bold" style={{ color: step.color }}>
                            {stepCompletedCount}/{step.items.length}
                          </span>
                        )}
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ background: `${step.color}12`, color: step.color }}>
                          {step.duration}
                        </span>
                      </div>
                    </div>
                    <h4 className="font-bold text-gray-800 text-sm leading-snug mt-0.5">{step.title}</h4>
                  </div>
                </div>

                {/* Mini progress bar for step */}
                {showProgress && (
                  <div className="h-1 bg-gray-100 rounded-full mb-3 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${(stepCompletedCount / step.items.length) * 100}%`,
                        backgroundColor: step.color,
                      }}
                    />
                  </div>
                )}

                <ul className="space-y-2">
                  {step.items.map((item, j) => {
                    const itemKey = `${step.step}-${j}`;
                    const isDone = !!completed[itemKey];
                    return (
                      <li
                        key={j}
                        className={`flex items-start gap-2 text-xs leading-relaxed transition-all ${
                          showProgress ? 'cursor-pointer hover:opacity-80' : ''
                        } ${isDone ? 'text-gray-400' : 'text-gray-600'}`}
                        onClick={() => showProgress && toggle(itemKey)}
                      >
                        <div className="w-3.5 h-3.5 flex items-center justify-center shrink-0 mt-0.5">
                          {showProgress ? (
                            <i
                              className={`text-xs ${isDone ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'}`}
                              style={{ color: isDone ? step.color : '#D1D5DB' }}
                            ></i>
                          ) : (
                            <i className="ri-checkbox-circle-fill text-xs" style={{ color: step.color }}></i>
                          )}
                        </div>
                        <span className={isDone && showProgress ? 'line-through' : ''}>{item}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
            })}
          </div>

          {/* CTA Banner */}
          <div
            className="mt-10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-4"
            style={{ background: 'linear-gradient(135deg, #2E1065, #4C1D95)' }}
          >
            <div>
              <h3 className="font-black text-white text-lg mb-1">{t('资金回流唯一合规通道', 'The Only Compliant Capital Repatriation Channel')}</h3>
              <p className="text-white/60 text-sm">
                {t('发行所得资金须通过', 'Proceeds must be repatriated via')} <strong className="text-[#F59E0B]">CIPS（{t('人民币跨境支付系统', 'Cross-border Interbank Payment System')}）</strong>
                {t('或', ' or ')} <strong className="text-[#F59E0B]">QFLP（{t('合格境外有限合伙人', 'Qualified Foreign Limited Partner')}）</strong>
                {t('渠道回流境内，任何其他路径均属违规。', ' — any other channel is non-compliant.')}
              </p>
            </div>
            <a
              href="#about"
              className="inline-flex items-center gap-2 px-6 py-3 font-bold rounded-full whitespace-nowrap cursor-pointer text-sm transition-all hover:scale-105 shrink-0"
              style={{ background: 'linear-gradient(135deg, #F59E0B, #FB923C)', color: '#1A0050' }}
            >
              <i className="ri-phone-line"></i>
              {t('咨询落地服务', 'Consult Landing Services')}
            </a>
          </div>
        </div>
      </section>

      {/* ======= 08 全行业上链指南 ======= */}
      <section id="industry" className="py-20 bg-[#F5F3FF]">
        <div className="max-w-[1440px] mx-auto px-4 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#7C3AED]/8 border border-[#7C3AED]/15 rounded-full mb-4">
              <i className="ri-grid-line text-[#7C3AED] text-sm"></i>
              <span className="text-[#7C3AED] text-sm font-semibold">{t('08 · 全行业上链指南', '08 · Cross-Industry On-Chain Guide')}</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">{t('全行业 RWA 上链路径速查', 'Cross-Industry RWA On-Chain Path Quick Reference')}</h2>
            <p className="text-gray-500 max-w-xl mx-auto text-base">{t('8大行业代币化路径简述，找到你所在行业的RWA切入点与合规红线。', '8 industry tokenization paths — find your industry\'s RWA entry point and compliance boundaries.')}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {industries.map((ind) => (
              <div
                key={ind.name}
                className="bg-white border border-gray-100 rounded-xl p-5 hover:border-[#7C3AED]/30 hover:-translate-y-0.5 transition-all cursor-pointer group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl" style={{ background: `${ind.color}15` }}>
                    <i className={`${ind.icon} text-xl`} style={{ color: ind.color }}></i>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[ind.priority]}`}>
                    {ind.priority}
                  </span>
                </div>
                <h4 className="font-black text-gray-800 mb-0.5 group-hover:text-[#7C3AED] transition-colors">{ind.name}</h4>
                <div className="text-xs text-gray-400 mb-3">{ind.sub}</div>
                <div className="text-xs text-gray-500 leading-relaxed border-t border-gray-50 pt-3">{ind.steps}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
